import React from 'react';

import {
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_BOOLEAN,
} from '../../util/types';

import { useConfiguration } from '../../context/configurationContext';
import appSettings from '../../config/settings';

import { useIntl } from '../../util/reactIntl';
import { required, nonEmptyArray, minOptionsArray } from '../../util/validators';
import { supportedParnexusCurrencies } from '../../util/currency';

import {
  FieldCheckboxGroup,
  FieldSelect,
  FieldTextInput,
  FieldBoolean,
  FieldCheckbox,
  FieldCurrencyInput,
} from '../../components';

import css from './CustomExtendedDataField.module.css';

const createFilterOptions = options => options.map(o => ({ key: `${o.option}`, label: o.label }));

const customValidators = {
  "pub_proofOfLife": minOptionsArray("Min 1 option is required.", 1),
  "pub_buyerProof": minOptionsArray("Min 1 option is required.", 1),
};

const getLabel = fieldConfig => fieldConfig?.saveConfig?.label || fieldConfig?.label;

const CustomFieldEnum = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl, customPlaceholders } = props;
  const { enumOptions = [], saveConfig } = fieldConfig || {};
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};

  const customValidator = customValidators[name];
  const validateMaybe = customValidator
    ? { validate: customValidator }
    : isRequired
      ? { validate: required(requiredMessage || defaultRequiredMessage) }
      : {};

  const placeholder =
    placeholderMessage || customPlaceholders ? intl.formatMessage({ id: `CustomExtendedDataField.${name}PlaceHolder` }) : intl.formatMessage({ id: 'CustomExtendedDataField.placeholderLong' });

  const filterOptions = createFilterOptions(enumOptions);

  const label = getLabel(fieldConfig);

  return filterOptions ? (
    <FieldSelect
      className={css.customField}
      name={name}
      id={formId ? `${formId}.${name}` : name}
      label={label}
      {...validateMaybe}
    >
      <option disabled value="">
        {placeholder}
      </option>
      {filterOptions.map(optionConfig => {
        const key = optionConfig.key;
        return (
          <option key={key} value={key}>
            {optionConfig.label}
          </option>
        );
      })}
    </FieldSelect>
  ) : null;
};

const CustomFieldMultiEnum = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, values = {} } = props;
  const { enumOptions = [], saveConfig } = fieldConfig || {};
  const { isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig);

  const customValidator = customValidators[name];
  const validateMaybe = customValidator
    ? { validate: customValidator }
    : isRequired
      ? { validate: nonEmptyArray(requiredMessage || defaultRequiredMessage) }
      : {};

  const IsKeyRegions = props.fieldConfig?.key === 'keyregions'
  const isDealSize = name == "pub_dealSizeBracketFilter";
  const isUndiclosed = !!values.poa;

  if (isDealSize && isUndiclosed && !enumOptions.find(o => o.option == "undisclosed")) {
    enumOptions.unshift({
      label: "Undisclosed", option: "undisclosed",
    });
  } else if (isDealSize && !isUndiclosed && enumOptions.find(o => o.option == "undisclosed")) {
    enumOptions.shift();
  };

  return enumOptions ? (
    <FieldCheckboxGroup
      // className={css.customField}
      className={`${css.customField} ${IsKeyRegions ? css.regions : ''}`}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      options={createFilterOptions(enumOptions)}
      {...validateMaybe}
    />
  ) : null;
};

const CustomFieldText = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl, customPlaceholders } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};

  const customValidator = customValidators[name];
  const validateMaybe = customValidator
    ? { validate: customValidator }
    : isRequired
      ? { validate: required(requiredMessage || defaultRequiredMessage) }
      : {};

  const label = getLabel(fieldConfig);
  const placeholder =
    placeholderMessage || customPlaceholders ? intl.formatMessage({ id: `CustomExtendedDataField.${name}PlaceHolder` }) : intl.formatMessage({ id: 'CustomExtendedDataField.placeholderLong' });

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="textarea"
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const formatNumberWithCommas = (value) => {
  if (!value) return '';
  const raw = value.toString().replace(/,/g, '');
  const number = parseInt(raw, 10);
  if (isNaN(number)) return '';
  return number.toLocaleString();
};

const parseNumber = (value) => {
  const raw = value.replace(/,/g, '');
  const parsed = parseInt(raw, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const CustomFieldLong = props => {
  const {
    name,
    fieldConfig,
    defaultRequiredMessage,
    formId,
    intl,
    customPlaceholders,
    values,
  } = props;

  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};

  const customValidator = customValidators[name];
  const validateMaybe = customValidator
    ? { validate: customValidator }
    : isRequired
      ? { validate: required(requiredMessage || defaultRequiredMessage) }
      : {};

  const label = getLabel(fieldConfig);
  const placeholder =
    placeholderMessage || customPlaceholders ? intl.formatMessage({ id: `CustomExtendedDataField.${name}PlaceHolder` }) : intl.formatMessage({ id: 'CustomExtendedDataField.placeholderLong' });

  const undisclosedSelected = values.poa;

  const config = useConfiguration();
  const marketplaceCurrency = config.currency;
  const currencyConfig = appSettings.getCurrencyFormatting(marketplaceCurrency);

  return (
    <>
      <div className={css.curencyFields}>
        {(props.fieldConfig?.key === 'Budget_minimum' || props.fieldConfig?.key === 'Budget_Maximum' ||
          props.fieldConfig?.key === 'asking_price'
        ) &&
          <FieldSelect id="editListingDetails.currency"
            name="currency"
            label="Currency"
            className={css.curency}
            defaultValue="GBP"
            validate={required(
              intl.formatMessage({
                id: 'EditListingDetailsForm.currencyRequired',
              })
            )}
          >
            <option disabled value="">Currency...</option>
            {supportedParnexusCurrencies.map(c => {
              return <option key={c} value={c}>{c}</option>
            })}
          </FieldSelect>}

        {undisclosedSelected && props.fieldConfig?.key === 'asking_price' ? <FieldTextInput
          className={css.customField}
          id="lockedPrice"
          name="lockedPrice"
          label={label}
          placeholder="Undisclosed"
          readonly="readonly"
        /> : <FieldCurrencyInput
          className={css.customField}
          id={formId ? `${formId}.${name}` : name}
          name={name}
          label={label}
          placeholder={placeholder}
          currencyConfig={currencyConfig}
          {...validateMaybe}
          skipCurrencyDisplay={true}
          hideErrorMessage={undisclosedSelected}
        />}

        {/* <FieldTextInput
          className={css.customField}
          id={formId ? `${formId}.${name}` : name}
          name={name}
          type="text"
          label={label}
          placeholder={placeholder}
          {...validateMaybe}
          parse={parseNumber}
          format={formatNumberWithCommas}
          render={({ input, meta }) => (
            <input
              {...input}
              value={formattedValue}
              onChange={(e) => handleChange(e, input)}
              className={css.customField}
              placeholder={placeholder}
            />
          )}
        /> */}

      </div>
      {props.fieldConfig?.key === 'asking_price' && (
        <div className={css.PoaWrapper}>
          <FieldCheckbox
            id="editListingDetails.poa"
            name="poa"
            label="Undisclosed"
            className={css.Poa}
          />
        </div>
      )}
    </>
  );
};

const CustomFieldBoolean = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};

  const customValidator = customValidators[name];
  const validateMaybe = customValidator
    ? { validate: customValidator }
    : isRequired
      ? { validate: required(requiredMessage || defaultRequiredMessage) }
      : {};

  const label = getLabel(fieldConfig);
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderBoolean' });

  return (
    <FieldBoolean
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

/**
 * Return Final Form field for each configuration according to schema type.
 *
 * These custom extended data fields are for generating input fields from configuration defined
 * in marketplace-custom-config.js. Other panels in EditListingWizard might add more extended data
 * fields (e.g. shipping fee), but these are independently customizable.
 *
 * @param {Object} props should contain fieldConfig that defines schemaType, enumOptions?, and
 * saveConfig for the field.
 */
const CustomExtendedDataField = props => {
  const intl = useIntl();
  const { enumOptions = [], schemaType } = props?.fieldConfig || {};
  const renderFieldComponent = (FieldComponent, props) => <FieldComponent {...props} intl={intl} />;

  return schemaType === SCHEMA_TYPE_ENUM && enumOptions
    ? renderFieldComponent(CustomFieldEnum, props)
    : schemaType === SCHEMA_TYPE_MULTI_ENUM && enumOptions
      ? renderFieldComponent(CustomFieldMultiEnum, props)
      : schemaType === SCHEMA_TYPE_TEXT
        ? renderFieldComponent(CustomFieldText, props)
        : schemaType === SCHEMA_TYPE_LONG
          ? renderFieldComponent(CustomFieldLong, props)
          : schemaType === SCHEMA_TYPE_BOOLEAN
            ? renderFieldComponent(CustomFieldBoolean, props)
            : null;
};

export default CustomExtendedDataField;
